import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { CheckCircle, Shield, MessageSquare, Clock, Zap, Lock } from 'lucide-react';

export default function Landing() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background via-background to-muted">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-background/80 backdrop-blur-sm border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-primary">In the Buff</h1>
          <div className="flex gap-3">
            <Button variant="outline" size="sm">Sign In</Button>
            <Button size="sm">Get Started</Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center space-y-6 mb-16">
            <h2 className="text-5xl sm:text-6xl font-bold text-foreground leading-tight">
              Premium Cleaning, Your Way
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Professional cleaning services with flexible attire options. Choose how our cleaners serve you—fully clothed, topless, lingerie, or nude.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button size="lg" className="gap-2">
                Book a Cleaner
              </Button>
              <Button size="lg" variant="secondary">
                Become a Cleaner
              </Button>
            </div>
          </div>

          {/* Hero Stats */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-16 pt-16 border-t border-border">
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">500+</p>
              <p className="text-muted-foreground">Verified Cleaners</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">2K+</p>
              <p className="text-muted-foreground">Happy Customers</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-primary">100%</p>
              <p className="text-muted-foreground">Satisfaction Rate</p>
            </div>
          </div>
        </div>
      </section>

      {/* Value Propositions */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-card">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12">Why Choose In the Buff?</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <CheckCircle className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">Your Choice Matters</h4>
                  <p className="text-muted-foreground">
                    Every customer has unique preferences. Our cleaners offer multiple attire options so you can choose exactly what's comfortable for you.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Shield className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">Verified & Trusted</h4>
                  <p className="text-muted-foreground">
                    All cleaners undergo thorough background checks, verification, and professional screening to ensure safety and quality.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Lock className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">Complete Privacy</h4>
                  <p className="text-muted-foreground">
                    Your preferences and service details remain entirely private and confidential. We never share information without consent.
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <Zap className="w-6 h-6 text-accent" />
                </div>
                <div>
                  <h4 className="font-semibold text-lg mb-2">Easy to Use</h4>
                  <p className="text-muted-foreground">
                    Simple booking, flexible scheduling, and real-time communication with your cleaner—all in one intuitive app.
                  </p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12">How It Works</h3>
          
          {/* For Customers */}
          <div className="mb-16">
            <h4 className="text-2xl font-semibold mb-8 text-primary">For Customers</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { step: '1', title: 'Browse', desc: 'Find cleaners matching your preferences' },
                { step: '2', title: 'Book', desc: 'Schedule your cleaning service' },
                { step: '3', title: 'Communicate', desc: 'Chat with your cleaner in real-time' },
                { step: '4', title: 'Relax', desc: 'Enjoy professional cleaning service' }
              ].map((item) => (
                <div key={item.step} className="relative">
                  <div className="bg-primary text-primary-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold mb-4">
                    {item.step}
                  </div>
                  <h5 className="font-semibold mb-2">{item.title}</h5>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                  {item.step !== '4' && (
                    <div className="hidden md:block absolute top-6 -right-2 w-4 h-0.5 bg-border" />
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* For Cleaners */}
          <div>
            <h4 className="text-2xl font-semibold mb-8 text-primary">For Cleaners</h4>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {[
                { step: '1', title: 'Join', desc: 'Sign up and complete verification' },
                { step: '2', title: 'Set Up', desc: 'Create your profile & choose preferences' },
                { step: '3', title: 'Accept Jobs', desc: 'Select bookings that match your schedule' },
                { step: '4', title: 'Earn', desc: 'Get paid for your professional service' }
              ].map((item) => (
                <div key={item.step} className="relative">
                  <div className="bg-accent text-accent-foreground rounded-full w-12 h-12 flex items-center justify-center font-bold mb-4">
                    {item.step}
                  </div>
                  <h5 className="font-semibold mb-2">{item.title}</h5>
                  <p className="text-sm text-muted-foreground">{item.desc}</p>
                  {item.step !== '4' && (
                    <div className="hidden md:block absolute top-6 -right-2 w-4 h-0.5 bg-border" />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-card">
        <div className="max-w-7xl mx-auto">
          <h3 className="text-3xl font-bold text-center mb-12">Core Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { icon: Clock, title: 'Smart Scheduling', desc: 'Book appointments that fit your schedule with real-time availability' },
              { icon: MessageSquare, title: 'In-App Chat', desc: 'Communicate securely with your cleaner before and during service' },
              { icon: Shield, title: 'Verified Profiles', desc: 'All cleaners verified with background checks and ratings' },
              { icon: Lock, title: 'Secure Payments', desc: 'Safe, encrypted payment processing for every transaction' },
              { icon: Zap, title: 'Custom Preferences', desc: 'Choose exactly how your cleaner serves you' },
              { icon: CheckCircle, title: 'Quality Guarantee', desc: 'Satisfaction guaranteed with our professional standards' }
            ].map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <Card key={idx} className="p-6">
                  <Icon className="w-8 h-8 text-primary mb-4" />
                  <h5 className="font-semibold mb-2">{feature.title}</h5>
                  <p className="text-sm text-muted-foreground">{feature.desc}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center space-y-8">
          <h3 className="text-4xl font-bold">Ready to Get Started?</h3>
          <p className="text-xl text-muted-foreground">
            Join thousands of satisfied customers and professional cleaners today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="gap-2">
              Book Your First Cleaning
            </Button>
            <Button size="lg" variant="outline">
              Apply as a Cleaner
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border py-12 px-4 sm:px-6 lg:px-8 bg-muted/30">
        <div className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 mb-8">
          <div>
            <h5 className="font-semibold mb-4">Customers</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-foreground transition">How It Works</a></li>
              <li><a href="#" className="hover:text-foreground transition">Browse Cleaners</a></li>
              <li><a href="#" className="hover:text-foreground transition">Pricing</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-semibold mb-4">Cleaners</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-foreground transition">Become a Cleaner</a></li>
              <li><a href="#" className="hover:text-foreground transition">Earn Money</a></li>
              <li><a href="#" className="hover:text-foreground transition">FAQ</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-semibold mb-4">Company</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-foreground transition">About Us</a></li>
              <li><a href="#" className="hover:text-foreground transition">Contact</a></li>
              <li><a href="#" className="hover:text-foreground transition">Blog</a></li>
            </ul>
          </div>
          <div>
            <h5 className="font-semibold mb-4">Legal</h5>
            <ul className="space-y-2 text-sm text-muted-foreground">
              <li><a href="#" className="hover:text-foreground transition">Privacy Policy</a></li>
              <li><a href="#" className="hover:text-foreground transition">Terms of Service</a></li>
              <li><a href="#" className="hover:text-foreground transition">Safety</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border pt-8 text-center text-sm text-muted-foreground">
          <p>&copy; 2026 In the Buff Cleaning Services. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
